import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  getInventorySourceOptions,
  resolveInventorySource,
  type InventorySourceValue,
} from '@/shared/inventoryTabSources';
import { saveInventorySource } from '@/utils/organization/saveInventorySource';
import { EditButton } from './EditButton';
import { SettingsRow } from './SettingsGroup';
import { missingInventorySourceNote } from '../reports/dataSourceNote';

// The "Inventory source" row, first in Reports and Charts › Data sources
// (FIG-442 · UC-251). One source only: choosing several would count the same
// physical stock twice. Saved through update-merchant-inventory-source-v1,
// which is admin-only and queues the report regeneration. The Inventory tab
// shows the saved value read-only and follows it.

interface InventorySourceRowProps {
  organisationId: string;
  /** organisations.connections: what the options are built from. */
  merchantConnections: unknown;
  /** organisations.inventory_source_config. */
  inventorySourceConfig: unknown;
  canEdit: boolean;
  onUpdated: () => void;
}

export const INVENTORY_SOURCE_DESCRIPTION =
  'Whose stock counts as Stock Held in reports, NERV and PO funding. Saving regenerates reports in the background.';

const SAVED_MESSAGE = 'Saved. Reports regenerate in the background.';

type Connections = Parameters<typeof resolveInventorySource>[1];

export const InventorySourceRow: React.FC<InventorySourceRowProps> = ({
  organisationId,
  merchantConnections,
  inventorySourceConfig,
  canEdit,
  onUpdated,
}) => {
  const connections = merchantConnections as Connections;
  const options = useMemo(() => getInventorySourceOptions(connections), [connections]);
  const resolved = useMemo(() => resolveInventorySource(inventorySourceConfig, connections), [inventorySourceConfig, connections]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<InventorySourceValue>('all');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ kind: 'saved' | 'warning' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (status?.kind !== 'saved') return;
    const timer = setTimeout(() => setStatus(null), 8000);
    return () => clearTimeout(timer);
  }, [status]);

  // Losing edit rights (admin mode turned off) drops any open edit.
  useEffect(() => {
    if (!canEdit) setEditing(false);
  }, [canEdit]);

  // With nothing but "each connection's own stock" there is nothing to choose,
  // unless a saved source is gone: then the admin can still save "each connection's own stock".
  const choosable = options.length > 1 || resolved.origin === 'fallback';
  const note = missingInventorySourceNote(resolved.origin);
  const isEditing = canEdit && editing;
  // Save stays off until the choice differs from what is saved (a fallback counts as different).
  const unchanged = draft === resolved.saved;

  const startEditing = () => {
    setDraft(resolved.value);
    setStatus(null);
    setEditing(true);
  };

  const save = async () => {
    if (!canEdit || unchanged) return;
    setSaving(true);
    const result = await saveInventorySource(organisationId, draft);
    setSaving(false);
    if (!result.ok) {
      setStatus({ kind: 'error', message: result.error ?? 'Could not save the inventory source' });
      return;
    }
    setEditing(false);
    if (result.regenerationError) {
      setStatus({
        kind: 'warning',
        message: `Saved, but ${result.regenerationError.charAt(0).toLowerCase()}${result.regenerationError.slice(1)}. Reports refresh the next time they are viewed.`,
      });
    } else {
      setStatus({ kind: 'saved', message: result.recorded ? SAVED_MESSAGE : `${SAVED_MESSAGE} Not recorded in history.` });
    }
    onUpdated();
  };

  return (
    <SettingsRow
      label="Inventory source"
      editing={isEditing}
      testId="report-source-inventory"
      action={canEdit && !isEditing && choosable && !saving ? (
        <EditButton label="Edit Inventory source" onClick={startEditing} />
      ) : null}
    >
      <div className="flex items-center gap-2">
        <span className="font-medium">{resolved.optionLabel}</span>
        {status?.kind === 'saved' && <span role="status" className="text-sm text-emerald-700">{status.message}</span>}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">{INVENTORY_SOURCE_DESCRIPTION}</div>
      {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
      {status?.kind === 'warning' && (
        <div role="alert" className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">{status.message}</div>
      )}
      {isEditing && (
        <div className="mt-3 space-y-3">
          <div className="max-w-[320px] space-y-1.5">
            <Label htmlFor="report-source-inventory-select">Inventory source</Label>
            <Select value={draft} onValueChange={(value) => setDraft(value as InventorySourceValue)} disabled={saving}>
              <SelectTrigger id="report-source-inventory-select" data-testid="inventory-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {status?.kind === 'error' && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <strong>Inventory source not saved.</strong> {status.message}. The previous source was kept.
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={save} disabled={saving || unchanged}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save source
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          </div>
        </div>
      )}
    </SettingsRow>
  );
};

export default InventorySourceRow;
